import React, { useState, useEffect, useCallback } from 'react';
import { useGetCrawledCuponsQuery } from 'state/crawled-coupons';
import TailwindDataTable from 'scenes/crawled_cupons/components/TailwindDataTable';
import Snackbar from 'components/Snackbar';
import AppBarWithFilters from '../components/AppBar';
import getColumns from '../list/columns';
import useFilteredData from '../components/FilteredData';
import { exportToCSV } from '../hooks/exportToCSV';
import ExportCouponsModal from '../components/ExportCouponsModal';
import TRActionModal from '../components/TRActionModal';

const NewCrawledCoupons = () => {
  const [page, setPage] = useState(0); // Começando pela primeira página
  const [pageSize, setPageSize] = useState(500); // 500 itens por página como padrão
  const [selectedSpider, setSelectedSpider] = useState("");
  const [spiderOptions, setSpiderOptions] = useState([]);
  const [isFilterOn, setIsFilterOn] = useState(true);
  const [filters, setFilters] = useState({ store_name: "", is_on_tr: "", is_new: "" });
  const [selectedRows, setSelectedRows] = useState([]);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isTRModalOpen, setIsTRModalOpen] = useState(false);
  const [trAction, setTrAction] = useState(null); // 'add' ou 'remove'

  const { data = [], isLoading, refetch } = useGetCrawledCuponsQuery(
    { new: true },
    { skip: false }
  );

  const availableFields = [
    { name: 'title', label: 'Título' },
    { name: 'description', label: 'Descrição' },
    { name: 'code', label: 'Código' },
    { name: 'url', label: 'URL' },
    { name: 'store_url', label: 'URL da Loja' },
    { name: 'store_name', label: 'Nome da Loja' },
    { name: 'spider', label: 'Spider' },
    { name: 'store_slug', label: 'SLUG' },
    { name: 'is_on_tr', label: 'TR?' },
    { name: 'last_time_seen', label: 'Visto por último' },
    { name: 'first_time_seen', label: 'Visto primeira vez' },
  ];
  const defaultSelectedFields = ['title', 'description', 'code', 'url'];

  const handleFiltersChange = useCallback((event) => {
    const { name, value } = event.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  }, []);

  const showSnackbar = (message) => {
    setSnackbarMessage(message);
    setOpenSnackbar(true);
  };

  const handleExportModal = () => {
    setIsExportModalOpen(true);
  };

  // Função para lidar com a exportação do modal
  const handleModalExport = (exportedData, selectedFields) => {
    console.log('Dados exportados:', exportedData);
    console.log('Campos selecionados:', selectedFields);
    
    showSnackbar(`Exportação concluída! ${selectedFields.length} campos exportados.`);
  };

  const ALL_SPIDERS = [
    "agoracupom",
    "ciadosdescontos",
    "cuponeria",
    "cuponomia",
    "descontocupom",
    "efacil_cupons",
    "ganhecupons",
    "kabum_cupons",
    "pegadesconto",
    "pelandocupons",
    "promobitcupons",
    "terabyteshop_cupons",
    "todoscupons"
  ];

  useEffect(() => {
    if (Array.isArray(data)) {
      const uniqueSpiders = Array.from(new Set([
        ...ALL_SPIDERS,
        ...data.map(item => item.spider)
      ])).filter(Boolean).sort();
      setSpiderOptions(uniqueSpiders);
    } else {
      setSpiderOptions(ALL_SPIDERS);
    }
  }, [data]);

  const filteredData = useFilteredData(data, selectedSpider, filters);

  const handleSpiderChange = (event) => {
    setSelectedSpider(event.target.value);
  };

  const handleExport = () => {
    const selectedData = filteredData.filter((row) => selectedRows.includes(row.id));
    exportToCSV(columns, selectedData.length > 0 ? selectedData : filteredData);
    
    showSnackbar(`${selectedData.length > 0 ? selectedData.length : filteredData.length} registros exportados com sucesso!`);
  };

  // Função para abrir modal de ações TR
  const handleTRAction = (action) => {
    if (selectedRows.length === 0) {
      showSnackbar('Selecione pelo menos um cupom para realizar esta ação.');
      return;
    }
    
    setTrAction(action);
    setIsTRModalOpen(true);
  };

  // Função para confirmar ação TR
  const handleTRConfirm = async (selectedData, action) => {
    try {
      console.log(`Ação ${action} para ${selectedData.length} cupons:`, selectedData);
      
      // Simulação de requisição (seu colega já deve ter implementado a real)
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Recarregar dados após ação
      refetch();
      
      const actionText = action === 'add' ? 'adicionados ao' : 'removidos do';
      showSnackbar(`${selectedData.length} cupom(s) ${actionText} TR com sucesso!`);
      
      // Limpar seleção
      setSelectedRows([]);
      
    } catch (error) {
      console.error('Erro na ação TR:', error);
      showSnackbar('Erro ao processar ação. Tente novamente.');
    }
  };

  const columns = getColumns(isFilterOn, filters, handleFiltersChange, spiderOptions, selectedSpider, handleSpiderChange);

  return (
    <div className="flex flex-col space-y-4 p-4 min-h-screen bg-gradient-to-br from-blue-50 to-purple-50">
      {/* Cabeçalho com gradiente de cores */}
      <div className="rounded-lg bg-gradient-to-r from-green-500 via-emerald-500 to-teal-500 p-6 shadow-lg">
        <h1 className="text-3xl font-bold tracking-tight text-white mb-2">
          Novos Cupons do Crawler
        </h1>
        <p className="text-green-100">
          Gerencie os cupons recém-capturados pelos crawlers
        </p>
        
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
          <div className="bg-white/90 backdrop-blur rounded-lg p-4 shadow-md border-l-4 border-green-500">
            <p className="text-sm font-medium text-gray-500">Total de Cupons Novos</p>
            <p className="text-2xl font-bold text-green-600">{data.length}</p>
          </div>
          <div className="bg-white/90 backdrop-blur rounded-lg p-4 shadow-md border-l-4 border-teal-500">
            <p className="text-sm font-medium text-gray-500">Cupons Filtrados</p>
            <p className="text-2xl font-bold text-teal-600">{filteredData.length}</p>
          </div>
          <div className="bg-white/90 backdrop-blur rounded-lg p-4 shadow-md border-l-4 border-emerald-500">
            <p className="text-sm font-medium text-gray-500">Spiders Disponíveis</p>
            <p className="text-2xl font-bold text-emerald-600">{spiderOptions.length}</p>
          </div>
          <div className="bg-white/90 backdrop-blur rounded-lg p-4 shadow-md border-l-4 border-lime-500">
            <p className="text-sm font-medium text-gray-500">Selecionados</p>
            <p className="text-2xl font-bold text-lime-600">{selectedRows.length}</p>
          </div>
        </div>
      </div>

      {/* Snackbar */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        message={snackbarMessage}
        onClose={() => setOpenSnackbar(false)}
        severity="success"
      />

      {/* AppBar estilizado com container */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="bg-gradient-to-r from-green-500 to-emerald-500 h-2"></div>
        <AppBarWithFilters
          selectedSpider={selectedSpider}
          spiderOptions={spiderOptions}
          handleSpiderChange={handleSpiderChange}
          handleExport={handleExport}
          handleExportModal={handleExportModal}
          handleTRAction={handleTRAction}
          selectedRowsCount={selectedRows.length}
          isRunning={false}
          showRunCommand={false}
          showAdvancedExport={true}
          showTRActions={true}
        />
      </div>

      {/* Tabela com container */}
      <div className="bg-white rounded-lg shadow-md overflow-visible">
        <div className="bg-gradient-to-r from-lime-500 to-green-500 h-2"></div>
        <TailwindDataTable
          data={filteredData}
          isLoading={isLoading}
          columns={columns}
          page={page}
          setPage={setPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          setSelectedRows={setSelectedRows}
          getRowId={(row) => row.id}
        />
      </div>
      
      {/* Rodapé - agora com padding-top para dar espaço à paginação */}
      <div className="text-center p-4 pt-12 text-sm text-gray-500">
        <p>Última atualização: {new Date().toLocaleString()}</p>
      </div>

      {/* Modais */}
      <ExportCouponsModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        data={selectedRows.length > 0 ? filteredData.filter(row => selectedRows.includes(row.id)) : filteredData}
        availableFields={availableFields}
        defaultSelectedFields={defaultSelectedFields}
        onExport={handleModalExport}
      />

      <TRActionModal
        isOpen={isTRModalOpen}
        onClose={() => setIsTRModalOpen(false)}
        selectedData={filteredData.filter(row => selectedRows.includes(row.id))}
        action={trAction}
        onConfirm={handleTRConfirm}
      />
    </div>
  );
};

export default NewCrawledCoupons;